import React from 'react';
import Tooltip from '@material-ui/core/Tooltip';
import IconButton from '@material-ui/core/IconButton';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import moment from 'moment';

export const ATTRIBUTION_TOOLTIPS = {
  learning_hub: 'The learner enrolled in this course through Learning Hub in this project.',
};

export const DOWNLOAD_ATTRIBUTION_COLUMNS = [
  { key: 'project_name', label: 'Project' },
  { key: 'company_name', label: 'Company' },
  { key: 'attribution_source', label: 'Attribution' },
  { key: 'is_email_sent', label: 'Email sent' },
  { key: 'email_sent_at', label: 'Email sent at' },
];

const DOWNLOAD_ATTRIBUTION_KEYS = new Set(DOWNLOAD_ATTRIBUTION_COLUMNS.map((col) => col.key));

export function attributionTooltip(source) {
  return ATTRIBUTION_TOOLTIPS[source] || '';
}

export function formatProjectLabel(row) {
  if (row?.project_id == null || row?.project_name == null) return '';
  return row.project_name;
}

export function formatCompanyLabel(row) {
  if (row?.company_id == null || row?.company_name == null) return '';
  return row.company_name;
}

export function formatTalentAccessEmail(row) {
  if (row?.is_email_sent === true) return 'Yes';
  if (row?.is_email_sent === false) return 'No';
  return '-';
}

export function formatEmailSentAt(value) {
  if (value == null || value === '') return '-';
  const parsed = moment(value);
  if (!parsed.isValid()) return '-';
  return parsed.format('D MMM YYYY, HH:mm');
}

const AttributionHint = ({ title }) => {
  if (!title) return null;
  return (
    <Tooltip title={title} placement="top" arrow>
      <IconButton size="small" aria-label={title} style={{ padding: 2, marginLeft: 2 }}>
        <InfoOutlinedIcon style={{ fontSize: 16, color: 'var(--dm-muted, #647184)' }} />
      </IconButton>
    </Tooltip>
  );
};

const cellStyle = { display: 'inline-flex', alignItems: 'center' };

export const attributionTableColumns = [
  {
    label: 'Project',
    key: 'project_name',
    csvKey: 'project_name',
    sortType: 'basic',
    render: (row) => (
      <span style={cellStyle}>
        {formatProjectLabel(row)}
        <AttributionHint title={attributionTooltip(row?.attribution_source)} />
      </span>
    ),
  },
  {
    label: 'Company',
    key: 'company_name',
    csvKey: 'company_name',
    sortType: 'basic',
    render: (row) => formatCompanyLabel(row),
  },
  {
    label: 'Email sent',
    key: 'is_email_sent',
    csvKey: 'is_email_sent',
    sortType: 'basic',
    render: (row) => formatTalentAccessEmail(row),
  },
  {
    label: 'Email sent at',
    key: 'email_sent_at',
    csvKey: 'email_sent_at',
    sortType: 'basic',
    render: (row) => formatEmailSentAt(row?.email_sent_at),
  },
];

export function withAttributionColumns(columns) {
  return [...columns, ...attributionTableColumns];
}

export function buildCustomerOptions(filter) {
  const structured = Array.isArray(filter?.customer_options) ? filter.customer_options : null;
  if (structured && structured.length) {
    return structured
      .filter((opt) => opt && opt.id != null && opt.id !== '')
      .map((opt) => ({
        id: opt.id,
        name: opt.name || String(opt.id),
      }));
  }
  const names = Array.isArray(filter?.customer) ? filter.customer : [];
  return names
    .filter((name) => name != null && name !== '')
    .map((name) => ({ id: name, name: String(name) }));
}

export function buildProjectOptions(filter) {
  const structured = Array.isArray(filter?.project_options) ? filter.project_options : null;
  if (structured && structured.length) {
    return structured
      .filter((opt) => opt && opt.id != null && opt.id !== '')
      .map((opt) => {
        const name = opt.name || String(opt.id);
        const companyName = opt.company_name || '';
        return {
          id: opt.id,
          name: companyName ? `${name} (${companyName})` : name,
          company_id: opt.company_id ?? null,
          company_name: companyName,
        };
      });
  }
  const names = Array.isArray(filter?.project) ? filter.project : [];
  return names
    .filter((name) => name != null && name !== '')
    .map((name) => ({
      id: name,
      name: String(name),
      company_id: null,
      company_name: '',
    }));
}

export function filterProjectOptions(projectOptions, selectedCustomerIds) {
  const options = Array.isArray(projectOptions) ? projectOptions : [];
  const selected = (selectedCustomerIds || []).filter((id) => id != null && id !== '');
  if (!selected.length) return options;
  const hasCompany = options.some((opt) => opt.company_id != null && opt.company_id !== '');
  if (!hasCompany) return options;
  const selectedSet = new Set(selected.map((id) => String(id)));
  return options.filter((opt) => (
    opt.company_id != null
    && opt.company_id !== ''
    && selectedSet.has(String(opt.company_id))
  ));
}

export function pruneProjectSelection(selectedProjectIds, allProjectOptions, selectedCustomerIds) {
  if (!Array.isArray(allProjectOptions) || !allProjectOptions.length) {
    return selectedProjectIds || [];
  }
  const visible = filterProjectOptions(allProjectOptions, selectedCustomerIds);
  const allowed = new Set(visible.map((opt) => String(opt.id)));
  return (selectedProjectIds || []).filter((id) => allowed.has(String(id)));
}

export function preferredCustomerSelection(filter) {
  const options = buildCustomerOptions(filter);
  return options.length ? options[0].id : null;
}

export function mergeOptionsById(previous, next) {
  if (!Array.isArray(next)) return Array.isArray(previous) ? previous : undefined;
  if (!next.length) return Array.isArray(previous) && previous.length ? previous : next;
  const merged = [];
  const seen = new Set();
  const push = (opt) => {
    if (!opt || opt.id == null || seen.has(String(opt.id))) return;
    seen.add(String(opt.id));
    merged.push(opt);
  };
  next.forEach(push);
  (Array.isArray(previous) ? previous : []).forEach(push);
  return merged;
}

export function mergeFilterOptions(previous, next) {
  if (!next) return previous;
  return {
    ...next,
    customer_options: mergeOptionsById(previous?.customer_options, next.customer_options),
    project_options: mergeOptionsById(previous?.project_options, next.project_options),
  };
}

export function normalizeCertificateOptions(options) {
  if (!Array.isArray(options)) return [];
  const seen = new Set();
  return options.map((opt, index) => {
    let label;
    let value;
    if (opt == null) {
      label = 'Unknown';
      value = null;
    } else if (typeof opt === 'string' || typeof opt === 'number') {
      label = String(opt);
      value = opt;
    } else if (typeof opt === 'object') {
      const rawValue = Object.prototype.hasOwnProperty.call(opt, 'value')
        ? opt.value
        : (opt.label ?? opt.name ?? null);
      value = rawValue == null ? null : rawValue;
      label = value == null ? 'Unknown' : (opt.label || opt.name || String(value));
    } else {
      return null;
    }
    const dedupeKey = value == null ? '__null__' : `v:${value}`;
    if (seen.has(dedupeKey)) return null;
    seen.add(dedupeKey);
    return { label, value, key: `certificate-${index}-${dedupeKey}` };
  }).filter(Boolean);
}

export function selectionIds(newVal) {
  if (!Array.isArray(newVal)) return [];
  return newVal.map((opt) => (opt && typeof opt === 'object' ? opt.id : opt));
}

export function certificateSelectionValues(newVal) {
  if (!Array.isArray(newVal)) return [];
  return newVal.map((opt) => (
    opt && typeof opt === 'object' && Object.prototype.hasOwnProperty.call(opt, 'value') ? opt.value : opt
  ));
}

export function formatDownloadAttributionCell(key, item) {
  if (key === 'is_email_sent') {
    const label = formatTalentAccessEmail(item);
    return label === '-' ? '' : label;
  }
  if (key === 'email_sent_at') return item?.email_sent_at || '';
  if (key === 'project_name') return formatProjectLabel(item);
  if (key === 'company_name') return formatCompanyLabel(item);
  const value = item?.[key];
  return value == null ? '' : value;
}

export function formatDownloadCell(field, item) {
  if (DOWNLOAD_ATTRIBUTION_KEYS.has(field.key)) {
    return formatDownloadAttributionCell(field.key, item || {});
  }
  return (item && item[field.key]) || 'N/A';
}

export function collectDownloadRows(data, fields) {
  const rows = [];
  if (!data || typeof data !== 'object') return rows;
  Object.keys(data).forEach((key) => {
    if (!Object.prototype.hasOwnProperty.call(data, key)) return;
    const value = data[key];
    const items = Array.isArray(value) ? value : [value];
    items.forEach((item) => {
      rows.push(fields.map((field) => formatDownloadCell(field, item)));
    });
  });
  return rows;
}

export function buildDownloadFields(columns) {
  return [
    ...columns.map((col) => ({ key: col.csvKey, label: col.label })),
    ...DOWNLOAD_ATTRIBUTION_COLUMNS,
  ];
}
